import { c as _c } from "react/compiler-runtime";
import { ChartContextOptions } from '#components/chart/context/chartContext';
import { useChartContext } from '#components/chart/context/useChartContext';
import { ParsedChartReturn } from '#components/chart/types';
import { ReferenceArea } from 'recharts';
import { Props } from 'recharts/types/cartesian/ReferenceArea';
type AreaConfig = {
  y1: number | undefined;
  y2: number | undefined;
};
type AreaPropsParams = {
  options: ChartContextOptions;
  targetIndicatorDirection: ParsedChartReturn['targetIndicatorDirection'];
};
const getHighlightColor = (highLightedZone: string | undefined, colors: ChartContextOptions['colors']) => {
  return highLightedZone === 'negative' ? colors.HIGHLIGHT_NEGATIVE : colors.HIGHLIGHT_POSITIVE;
};
const getBiggerBetterArea = (bigger: AreaConfig, smaller: AreaConfig, highLightedZone: string | undefined, reversed: boolean, color: string) => {
  if (highLightedZone === 'negative') {
    return {
      fill: color,
      ...(reversed ? bigger : smaller)
    };
  }
  return {
    fill: color,
    ...(reversed ? smaller : bigger)
  };
};
const getSmallerBetterArea = (bigger: AreaConfig, smaller: AreaConfig, highLightedZone: string | undefined, reversed: boolean, color: string) => {
  if (highLightedZone === 'negative') {
    return {
      fill: color,
      ...(reversed ? smaller : bigger)
    };
  }
  return {
    fill: color,
    ...(reversed ? bigger : smaller)
  };
};
const getAreaProps = (bigger: AreaConfig, smaller: AreaConfig, {
  options,
  targetIndicatorDirection
}: AreaPropsParams) => {
  const {
    highLightedZone,
    reversed,
    colors
  } = options;
  const color = getHighlightColor(highLightedZone, colors);
  const areaProps = {
    bigger_better: () => getBiggerBetterArea(bigger, smaller, highLightedZone, reversed, color),
    smaller_better: () => getSmallerBetterArea(bigger, smaller, highLightedZone, reversed, color)
  };
  if (!targetIndicatorDirection) {
    return {};
  }
  return areaProps[targetIndicatorDirection]?.() ?? {};
};
export const HighlightedArea = props => {
  const $ = _c(10);
  const {
    data,
    options
  } = useChartContext();
  const {
    targetPeriodConfig,
    min,
    max,
    targetIndicatorDirection
  } = data;
  if (!options.highLightedZone || options.highLightedZone === "none" || !targetPeriodConfig.descriptorPoint || !targetIndicatorDirection) {
    return null;
  }
  const t0 = data.yAxisTicks[data.yAxisTicks.length - 1];
  let t1;
  if ($[0] !== options || $[1] !== t0 || $[2] !== targetIndicatorDirection || $[3] !== targetPeriodConfig.descriptorPoint.y) {
    const biggerArea = {
      y1: targetPeriodConfig.descriptorPoint.y,
      y2: t0
    };
    const smallerArea = {
      y1: 0,
      y2: targetPeriodConfig.descriptorPoint.y
    };
    t1 = getAreaProps(biggerArea, smallerArea, {
      options,
      targetIndicatorDirection
    });
    $[0] = options;
    $[1] = t0;
    $[2] = targetIndicatorDirection;
    $[3] = targetPeriodConfig.descriptorPoint.y;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const areaProps = t1;
  let t2;
  if ($[5] !== areaProps || $[6] !== max || $[7] !== min || $[8] !== props) {
    t2 = <ReferenceArea {...props} ifOverflow="visible" x1={min} x2={max} {...areaProps} stroke="none" />;
    $[5] = areaProps;
    $[6] = max;
    $[7] = min;
    $[8] = props;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};

// Workaround for being able to extend the component
// See: https://github.com/recharts/recharts/issues/412
HighlightedArea.displayName = ReferenceArea.displayName;
HighlightedArea.defaultProps = ReferenceArea.defaultProps;